import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import authService, { type RegisterRequest } from "@/services/auth.service";
import userService, { ProfileResponse } from "@/services/user.service";
import teamService, { type TeamScope } from "@/services/team.service";
import {
  getMaintenanceStatusApiV1AdminSettingsMaintenanceModeStatusGet,
  getMyProfileApiV1UsersMeGet,
} from "@/backend-sdk/client/sdk.gen";
import {
  clearHeyApiSubAccountToken,
  getHeyApiSubAccountToken,
  heyAdminApiClient,
  isHeyApiError,
  setAuthReady,
} from "@/lib/hey-api-client";
import { authDbg } from "@/lib/auth-debug-logger";
import {
  accountAccessContextFor,
  clearAccountAccessContext,
  ehContextoDeTime,
  contaPadraoDoDono,
  ehDonoDasContas,
  ehPlanoDeAgencia,
  getAccountAccessContext,
  isMultiAccountAccessContext,
  isSwitchedAccessContext,
  setAccountAccessContext,
} from "@/lib/account-access-context";
import {
  ehMembroDeTime,
  lojaPadraoDoMembro,
  navegaPorEscopoCedido,
  podeGerirTimes,
} from "@/lib/team-access";
import { toast } from "@/hooks/use-toast";

/* eslint-disable react-refresh/only-export-components */

interface AuthContextType {
  user: ProfileResponse | null;
  loading: boolean;
  isAuthenticated: boolean;
  /**
   * De que times o usuário faz parte e que lojas ele alcança por eles, ou `null` enquanto o
   * perfil não carregou.
   *
   * `{ teams: [], accounts: [] }` é resposta legítima e o caso comum — quem não é membro de
   * time nenhum, o assinante incluído. Para o membro, `accounts` é a **única** fonte dos
   * `customer_uuid` que ele pode usar como tenant: `GET /api/v1/accounts` responde
   * `403 Usuário não é um assinante` para ele.
   */
  teamScope: TeamScope | null;
  /** É membro de algum time da agência — o funcionário, nunca o assinante. */
  isTeamMember: boolean;
  /** Vê as telas de gestão de times (o assinante, ou quem tem papel `admin`). */
  canManageTeams: boolean;
  /*
   * <M-248> `isReadOnly` **saiu daqui** e virou `useAccount().isReadOnly`. Ele era o
   * predicado da navegação por escopo cedido usado como predicado da escrita, o que valia
   * enquanto a trilha recusava toda escrita; desde <D-061> quem decide é o `access_level`
   * da pessoa **naquela loja**, e a loja ativa é do contexto de contas, não deste.
   */
  isMaintenanceMode: boolean;
  maintenanceMessage: string;
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  verify2FA: (code: string) => Promise<void>;
  checkMaintenanceStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const hasMaintenanceDetail = (
  detail: unknown,
): detail is { maintenance_mode: true; message: string } => {
  return Boolean(
    detail &&
    typeof detail === "object" &&
    "maintenance_mode" in detail &&
    (detail as { maintenance_mode?: unknown }).maintenance_mode &&
    "message" in detail &&
    typeof (detail as { message?: unknown }).message === "string",
  );
};

const getErrorDescription = (error: unknown, fallback: string) => {
  if (isHeyApiError(error)) {
    if (typeof error.detail === "string") return error.detail;
    if (
      error.detail &&
      typeof error.detail === "object" &&
      "message" in error.detail
    ) {
      const message = (error.detail as { message?: unknown }).message;
      if (typeof message === "string") return message;
    }
  }
  return fallback;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ProfileResponse | null>(null);
  const [teamScope, setTeamScope] = useState<TeamScope | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState(
    "O sistema está em manutenção. Tente novamente em alguns instantes.",
  );
  const navigate = useNavigate();

  /**
   * O contexto de contas de quem navega por **escopo cedido**, que é o quarto ramo desta
   * função e o único em que o usuário não é dono de conta nenhuma.
   *
   * São duas pessoas, e a marca gravada é a mesma para as duas (`team_member`, o nome que
   * ela tem desde os times): o **funcionário** que alcança as lojas dos times dele e a
   * **credencial cedida ao cliente**, que alcança as lojas que a agência lhe marcou. As
   * duas leem `my-scope`, as duas navegam trocando o `customer_uuid`, e as duas são de
   * leitura.
   *
   * Sem este ramo, as duas cairiam no ramo final de `applyAccountContext`, que não oferece
   * seletor e escolheria a primeira conta do perfil — que essas pessoas não têm. O ramo
   * próprio grava a marca, que é o que faz o seletor de lojas aparecer, e escolhe uma loja
   * para que nenhuma tela fique sem tenant no meio do caminho.
   *
   * <D-062> O tenant é o `accounts.id` da loja, e não mais o `users.id` do dono dela — o
   * `customer_uuid` que `my-scope` devolve **é** o `account_id`.
   */
  const applyTeamContext = (escopo: TeamScope) => {
    setAccountAccessContext("team_member");

    const padrao = lojaPadraoDoMembro(
      escopo,
      localStorage.getItem("active_subaccount_id"),
    );

    if (padrao?.customer_uuid) {
      localStorage.setItem("active_subaccount_id", padrao.account_id);
      localStorage.setItem("active_customer_uuid", padrao.customer_uuid);
    }
  };

  /**
   * Carrega o escopo de times e o aplica.
   *
   * Devolve **duas** respostas, e a segunda é a que evita um estado ruim: `conhecido` diz
   * se o servidor chegou a responder. "Não é membro de time nenhum" e "não deu para
   * perguntar" levam os dois a `navega: false`, mas pedem tratamentos opostos no contexto
   * de contas — um limpa o que ficou do time, o outro preserva.
   *
   * Falha de rede aqui **não** derruba o login: sem a resposta, o app trata quem entrou
   * como não-membro, e o pior que acontece é a interface não oferecer as lojas do time até
   * o próximo carregamento. O contrário — barrar o login porque uma chamada auxiliar caiu —
   * trancaria todo mundo para fora por causa de um recurso que só parte dos usuários usa.
   */
  const applyTeamScope = async (
    profile: ProfileResponse,
  ): Promise<{ navega: boolean; conhecido: boolean }> => {
    try {
      const escopo = await teamService.getMyScope();
      setTeamScope(escopo);
      if (navegaPorEscopoCedido(profile, escopo)) {
        applyTeamContext(escopo);
        return { navega: true, conhecido: true };
      }
      return { navega: false, conhecido: true };
    } catch (error) {
      authDbg(`[AUTH_DBG] applyTeamScope: falhou, seguindo sem: ${error}`);
      setTeamScope(null);
      return { navega: false, conhecido: false };
    }
  };

  const applyAccountContext = (
    profile: ProfileResponse,
    opcoes: { escopoDeTimeConhecido: boolean } = {
      escopoDeTimeConhecido: true,
    },
  ) => {
    const isAgencyPlan = ehPlanoDeAgencia(profile.subscription?.plan_category);
    const hasSubAccountToken = !!localStorage.getItem(
      "subaccount_access_token",
    );
    const existingContext = getAccountAccessContext();

    // Se já estamos num contexto de troca de conta (subconta selecionada), preservar.
    // Isso evita que o /users/me da subconta apague o contexto do dono.
    const isInMultiAccountContext =
      isMultiAccountAccessContext(existingContext);

    // <M-214> Mais de uma conta **própria** é o que dá direito ao seletor, e isso não é
    // privilégio de agência: todo plano `lojista` permite até cinco contas. Antes, só a
    // categoria do plano contava, e um lojista com três contas caía no `else` abaixo — que
    // apaga o contexto e força `accounts[0]` a cada carregamento do perfil.
    const hasMultipleAccounts =
      (profile.accounts?.length ?? 0) > 1 && ehDonoDasContas(profile.accounts);

    if (isAgencyPlan || hasMultipleAccounts) {
      setAccountAccessContext(
        accountAccessContextFor({
          isAgency: isAgencyPlan,
          isSwitched: hasSubAccountToken,
        }),
      );

      if (!hasSubAccountToken) {
        if (isAgencyPlan) {
          // A conta principal da agência não tem dados próprios: fica sem tenant.
          localStorage.removeItem("active_subaccount_id");
          localStorage.removeItem("active_customer_uuid");
        } else {
          // <M-214> O lojista multi-conta **não** tem conta principal: toda tela dele
          // pede uma conta. O seletor ainda vai perguntar qual, mas `active_customer_uuid`
          // não pode ficar vazio no meio do caminho — dezenas de telas o leem como tenant,
          // e algumas quebram sem ele. Vale a última escolhida, ou a primeira.
          const padrao = contaPadraoDoDono(
            profile.accounts,
            localStorage.getItem("active_subaccount_id"),
          );

          if (padrao) {
            localStorage.setItem("active_subaccount_id", padrao.id);
            // <D-062> O tenant é a conta. O fallback era `profile.id`, de quando ele era o
            // `users.id` do dono; agora a própria conta responde por si.
            localStorage.setItem(
              "active_customer_uuid",
              padrao.customer_uuid || padrao.id,
            );
          }
        }
      }
    } else if (
      ehContextoDeTime(existingContext) &&
      !opcoes.escopoDeTimeConhecido
    ) {
      // O membro de time já teve o tenant escolhido por `applyTeamContext`, e chegar aqui
      // com o escopo **desconhecido** significa que `my-scope` não respondeu desta vez —
      // rede caída, servidor lento. O ramo final gravaria `active_customer_uuid` com o id
      // da primeira conta do perfil — que o funcionário não tem —, e a tela seguinte mostraria
      // erro em vez da loja que ele estava vendo. Preservar o que está no navegador é o
      // comportamento certo: a próxima carga de perfil refaz a escolha.
      //
      // A conferência de `escopoDeTimeConhecido` é o que separa isso de "o servidor
      // respondeu e ele não é mais membro" — tirado do time, ou com o time desativado.
      // Nesse caso preservar seria pior do que limpar: a marca `team_member` ficaria para
      // sempre no navegador e a tela continuaria mandando o tenant de uma loja que a API
      // já recusa, sem nada que conserte a não ser sair e entrar de novo.
      authDbg(
        "[AUTH_DBG] applyAccountContext: preservando o contexto de time (my-scope não respondeu)",
      );
    } else if (isInMultiAccountContext && hasSubAccountToken) {
      // Perfil veio da subconta, mas estamos no contexto do dono: NÃO apagar.
      // O /users/me retornou o perfil da subconta (uma conta só, sem assinatura),
      // e é o contexto persistido que sabe que há um dono com várias contas atrás.
      console.log(
        "🔍 applyAccountContext: Preservando contexto de múltiplas contas (perfil da subconta)",
      );
    } else {
      clearAccountAccessContext();

      const firstAccount =
        profile.accounts && profile.accounts.length > 0
          ? profile.accounts[0]
          : null;

      if (firstAccount?.id) {
        localStorage.setItem("active_subaccount_id", firstAccount.id);
      } else {
        localStorage.removeItem("active_subaccount_id");
      }

      // <D-062> Sem conta não há tenant: o `|| profile.id` que ficava aqui gravava o
      // `users.id` de quem entrou, e esse valor a API não reconhece mais como loja nenhuma.
      const customerUuid = firstAccount?.customer_uuid || firstAccount?.id;
      if (customerUuid) {
        localStorage.setItem("active_customer_uuid", customerUuid);
      } else {
        localStorage.removeItem("active_customer_uuid");
      }
    }
  };

  const loadUser = async () => {
    try {
      if (authService.isAuthenticated()) {
        setAuthReady();

        const accessContext = getAccountAccessContext();
        // <M-214> Vale para o lojista multi-conta tanto quanto para a agência: o que
        // importa é haver uma conta selecionada por cima da do dono.
        const isInSwitchedContext = isSwitchedAccessContext(accessContext);

        authDbg(
          `[AUTH_DBG] loadUser: accessContext=${accessContext} isInSwitchedContext=${isInSwitchedContext} hasSubAccountToken=${!!getHeyApiSubAccountToken()}`,
        );

        // Se há uma conta selecionada, buscar o perfil com o token do dono (adminApi)
        // para manter a assinatura e a lista completa de contas na sessão.
        let profile: ProfileResponse;
        if (isInSwitchedContext) {
          authDbg(
            "[AUTH_DBG] loadUser: fetching profile with heyAdminApiClient (admin token)",
          );
          const { data } = await getMyProfileApiV1UsersMeGet({
            client: heyAdminApiClient,
            throwOnError: true,
          });
          profile = data;
        } else {
          authDbg(
            "[AUTH_DBG] loadUser: fetching profile with userService.getMyProfile (default client)",
          );
          profile = await userService.getMyProfile();
        }

        authDbg(
          `[AUTH_DBG] loadUser: profile loaded email=${profile.email} accounts=${Array.isArray(profile.accounts) ? profile.accounts.length : "UNDEFINED"} plan_category=${profile.subscription?.plan_category}`,
        );

        // A ordem importa por dois motivos.
        //
        // O ramo do membro de time **substitui** o de contas, e não se soma a ele:
        // `applyAccountContext` gravaria `active_customer_uuid` com o id do próprio
        // funcionário, que é um tenant que a API recusa.
        //
        // E o perfil só é publicado **depois** do escopo. Publicá-lo antes daria ao
        // `AccountProvider` uma renderização inteira com `user` preenchido e `teamScope`
        // ainda nulo: o efeito dele cairia no ramo de quem não é membro e regravaria
        // `active_subaccount_id` com a conta principal da agência. Quando o escopo
        // chegasse, `lojaPadraoDoMembro` não reconheceria mais a loja escolhida e o membro
        // voltaria para a primeira da lista a cada recarregamento.
        const escopo = await applyTeamScope(profile);
        if (!escopo.navega) {
          applyAccountContext(profile, {
            escopoDeTimeConhecido: escopo.conhecido,
          });
        }

        setUser(profile);
      }
    } catch (error: unknown) {
      console.error("Erro ao carregar usuário:", error);
      // Apenas limpar tokens se for erro de autenticação
      if (isHeyApiError(error) && error.status === 401) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
      }
    } finally {
      setAuthReady();
      setLoading(false);
    }
  };

  // Carregar perfil do usuário ao iniciar
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await authService.login({ email, password });

      // Resetar contexto de acesso ao realizar um novo login
      clearAccountAccessContext();
      localStorage.removeItem("account_selection");
      clearHeyApiSubAccountToken();
      localStorage.removeItem("active_subaccount_id");
      localStorage.removeItem("active_customer_uuid");

      if (response.needs_2fa) {
        // Redirecionar para página de 2FA
        navigate("/2fa");
        return false;
      }

      // Carregar perfil do usuário
      const profile = await userService.getMyProfile();

      // Mesma ordem de `loadUser`: o escopo antes do perfil, para que o contexto de contas
      // nunca veja um membro de time sem as lojas dele.
      const escopo = await applyTeamScope(profile);
      if (!escopo.navega) {
        applyAccountContext(profile, {
          escopoDeTimeConhecido: escopo.conhecido,
        });
      }

      setUser(profile);

      toast({
        title: "Login realizado com sucesso!",
        description: "Bem-vindo de volta.",
      });

      // Determinar rota de redirecionamento baseada em integrações e tipo de plano
      const redirectRoute = await determineRedirectRoute();
      navigate(redirectRoute);
      return true;
    } catch (error: unknown) {
      // Check if it's a maintenance mode error
      if (
        isHeyApiError(error) &&
        error.status === 503 &&
        hasMaintenanceDetail(error.detail)
      ) {
        setIsMaintenanceMode(true);
        setMaintenanceMessage(error.detail.message);
        return false;
      }

      // Não fazer nada aqui que possa causar reload
      // O toast já é mostrado pelo contexto
      toast({
        title: "Erro ao fazer login",
        description: getErrorDescription(error, "Verifique suas credenciais."),
        variant: "destructive",
      });
      return false;
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      await authService.register(data);

      toast({
        title: "Conta criada com sucesso!",
        description: "Faça login para continuar.",
      });

      navigate("/login");
    } catch (error: unknown) {
      // Check if it's a maintenance mode error
      if (
        isHeyApiError(error) &&
        error.status === 503 &&
        hasMaintenanceDetail(error.detail)
      ) {
        setIsMaintenanceMode(true);
        setMaintenanceMessage(error.detail.message);
        return;
      }

      toast({
        title: "Erro ao criar conta",
        description: getErrorDescription(error, "Tente novamente."),
        variant: "destructive",
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setTeamScope(null);
      localStorage.clear();
      navigate("/login");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const verify2FA = async (code: string) => {
    try {
      await authService.verify2FA(code);

      // Carregar perfil do usuário após verificação 2FA
      await loadUser();

      toast({
        title: "Autenticação realizada com sucesso!",
        description: "Bem-vindo ao Painel do E-Commerce.",
      });

      // Determinar rota de redirecionamento baseada em integrações e tipo de plano
      const redirectRoute = await determineRedirectRoute();
      navigate(redirectRoute);
    } catch (error: unknown) {
      toast({
        title: "Código inválido",
        description: "Verifique o código no seu dispositivo e tente novamente.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const refreshProfile = async () => {
    await loadUser();
  };

  const determineRedirectRoute = async (): Promise<string> => {
    if (!user) {
      console.log("🔍 determineRedirectRoute: usuário não encontrado");
      return "/dashboard"; // Fallback
    }

    console.log("🔍 determineRedirectRoute: usuário encontrado:", user.email);
    console.log("🔍 determineRedirectRoute: user_type:", user.user_type);
    console.log("🔍 determineRedirectRoute: subscription:", user.subscription);
    console.log(
      "🔍 determineRedirectRoute: accounts:",
      user.accounts?.length || 0,
    );

    // Se o usuário não é um subscriber, não aplicar lógica de redirecionamento
    if (user.user_type !== "subscriber" && user.user_type !== "SUBSCRIBER") {
      console.log(
        "🔍 determineRedirectRoute: usuário não é subscriber, redirecionando para dashboard",
      );
      return "/dashboard";
    }

    // Verificar somente pelo plano
    if (user.subscription) {
      const planCategory = user.subscription.plan_category;
      console.log(
        "🔍 determineRedirectRoute: categoria do plano:",
        planCategory,
      );
      console.log(
        "🔍 determineRedirectRoute: subscription data:",
        user.subscription,
      );

      if (ehPlanoDeAgencia(planCategory)) {
        // Verificar se tem subcontas cadastradas
        const hasSubaccounts =
          Array.isArray(user.accounts) && user.accounts.length > 0;
        authDbg(
          "🔍 determineRedirectRoute: agência tem subcontas?",
          hasSubaccounts,
        );

        if (!hasSubaccounts) {
          // Redirecionar para cadastro de contas se não tem subcontas
          authDbg(
            "🔍 determineRedirectRoute: agência sem subcontas, redirecionando para /dashboard/accounts",
          );
          return "/dashboard/accounts";
        }
        // Se tem subcontas, ir para o dashboard (o modal de seleção vai pedir a escolha)
        return "/dashboard";
      }

      // <M-214> Lojista com mais de uma conta própria cai no mesmo caminho: mandá-lo
      // direto para as integrações seria configurar a conta que ele nem escolheu.
      if ((user.accounts?.length ?? 0) > 1 && ehDonoDasContas(user.accounts)) {
        return "/dashboard";
      }

      return "/dashboard/integrations";
    }

    // <M-251> Aqui havia um ramo para o `SUBSCRIBER` **sem assinatura** que devolvia
    // `/integrations` — uma rota que não existe. O painel só monta `/dashboard/*`, então
    // todo mundo que caía nele via um `404` logo depois de entrar, e o ramo pega justamente
    // quem a agência cria: o funcionário de time, a credencial de cliente e o **dono da
    // loja** nascem `user_type = SUBSCRIBER` e sem linha em `subscriptions`.
    //
    // `/dashboard` é o destino certo para os três, e não uma escolha conservadora: quem
    // precisa configurar integração chega lá pelo `IntegrationGuard`, que manda para
    // `/dashboard/integrations` quando a loja não tem conexão ativa — era essa a intenção
    // do ramo —, e quem só acompanha os painéis passa direto por ele.
    return "/dashboard";
  };

  const checkMaintenanceStatus = useCallback(async () => {
    try {
      const { data } =
        await getMaintenanceStatusApiV1AdminSettingsMaintenanceModeStatusGet({
          client: heyAdminApiClient,
          headers: { "x-ignore-global-filters": "1" },
          throwOnError: true,
        });
      setIsMaintenanceMode(data.is_enabled);
      setMaintenanceMessage(
        data.maintenance_message ||
          "O sistema está em manutenção. Tente novamente em alguns instantes.",
      );
    } catch (error) {
      // If we can't reach the maintenance endpoint, assume no maintenance
      setIsMaintenanceMode(false);
    }
  }, []);

  const isTeamMember = ehMembroDeTime(user, teamScope);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    teamScope,
    isTeamMember,
    canManageTeams: podeGerirTimes(user),
    isMaintenanceMode,
    maintenanceMessage,
    login,
    register,
    logout,
    refreshProfile,
    verify2FA,
    checkMaintenanceStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
